import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { UserPlus, UserCheck, Share2, Copy, Check, X } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/apiClient";
import { useAuth } from "../../context/AuthContext";

export const shortName = (c) => (c && (c.preferred_name || c.display_name)) || "";
export const money = (n) => (n == null ? null : `$${Number(n).toLocaleString()}`);

export function DemoNotice({ className = "" }) {
  return <span data-testid="demo-collection-notice" className={`inline-flex items-center rounded-full bg-cream border border-line px-2.5 py-0.5 text-[11px] font-bold text-ink-muted ${className}`}>Demo collection · illustrative products and prices · nothing for sale</span>;
}

export function Avatar({ initials, size = "md" }) {
  const cls = size === "lg" ? "h-16 w-16 text-2xl" : size === "sm" ? "h-8 w-8 text-xs" : "h-11 w-11 text-sm";
  return <span aria-hidden="true" className={`${cls} shrink-0 rounded-full bg-brand text-white font-heading font-black flex items-center justify-center`}>{initials}</span>;
}

// One Follow button everywhere; state is server-backed so profile / collection / Know Me stay in sync on reload.
export function FollowButton({ handle, following, onChange, name, shortLabel, small }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    if (!user) { localStorage.setItem("pending_auth_return", window.location.pathname + window.location.search); navigate("/auth"); return; }
    setBusy(true);
    try {
      const { data } = following ? await api.delete(`/contributors/${handle}/follow`) : await api.post(`/contributors/${handle}/follow`);
      onChange?.(data.following);
      toast.success(data.following ? `Following ${name}. Saved under Know Me — no email or texts.` : `Unfollowed ${name}`);
    } catch { toast.error("Could not update follow"); } finally { setBusy(false); }
  };
  return (
    <button onClick={toggle} disabled={busy} data-testid={`follow-${handle}`} aria-pressed={!!following}
      className={`inline-flex items-center gap-1.5 ${small ? "h-9 px-3 text-xs" : "h-11 px-4 text-sm"} rounded-full font-bold transition-colors ${following ? "bg-white border-2 border-brand text-brand hover:bg-blush/50" : "bg-brand text-white hover:bg-brand-hover"}`}>
      {following ? <UserCheck size={14} /> : <UserPlus size={14} />} {following ? "Following" : `Follow ${shortLabel || name}`}
    </button>
  );
}

export function CopyRow({ label, url, testid }) {
  const [done, setDone] = useState(false);
  const copy = async () => { try { await navigator.clipboard.writeText(url); setDone(true); setTimeout(() => setDone(false), 1500); } catch { toast.error("Copy failed"); } };
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 min-w-0"><p className="text-xs font-bold text-ink">{label}</p><p className="text-xs text-ink-muted truncate" data-testid={`${testid}-url`}>{url}</p></div>
      <button onClick={copy} data-testid={testid} className="h-10 px-3 rounded-full border border-line bg-white text-xs font-bold text-ink-soft hover:text-brand inline-flex items-center gap-1">{done ? <Check size={14} /> : <Copy size={14} />} {done ? "Copied" : "Copy"}</button>
    </div>
  );
}

export function Modal({ title, onClose, children, testid }) {
  return (
    <div role="dialog" aria-modal="true" aria-label={title} className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-ink/40 p-0 sm:p-4" onClick={onClose} data-testid={testid}>
      <div className="w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-3xl p-5 sm:p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3"><h3 className="font-heading font-extrabold text-lg text-ink">{title}</h3><button onClick={onClose} aria-label="Close" data-testid={`${testid}-close`} className="h-10 w-10 rounded-full hover:bg-cream flex items-center justify-center"><X size={18} /></button></div>
        <div className="mt-3 space-y-3">{children}</div>
      </div>
    </div>
  );
}

export function ShareButton({ collection, look }) {
  const [open, setOpen] = useState(false);
  const base = window.location.origin;
  const cUrl = `${base}/api/share/c/${collection.id}`; // server-rendered preview (title/image) → opens /c/:id
  const lUrl = look ? `${cUrl}?look=${look.id}` : null;
  const invite = `${base}/contribute?collection=${collection.id}`;
  const native = async (url, title) => { if (navigator.share) { try { await navigator.share({ title, url }); } catch { /* cancelled */ } } else { await navigator.clipboard.writeText(url); toast.success("Link copied"); } };
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="share-collection-btn" className="inline-flex items-center gap-1.5 h-11 px-4 rounded-full border border-line bg-white text-sm font-bold text-ink-soft hover:text-brand"><Share2 size={15} /> Share this collection</button>
      {open && (
        <Modal title="Send a look. Keep your family private." onClose={() => setOpen(false)} testid="share-dialog">
          <p className="text-sm text-ink-soft">{collection.name} — coordinated outfits and optional finishing touches. Child names, measurements and saved sizes are never included.</p>
          <CopyRow label="Collection" url={cUrl} testid="share-copy-collection" />
          {lUrl && <CopyRow label={`${shortName(look.contributor)}'s approved look`} url={lUrl} testid="share-copy-look" />}
          <CopyRow label="Invite someone to suggest accessories" url={invite} testid="share-copy-invite" />
          {look && <p className="text-xs text-ink-muted">Sharing a contributor look keeps their disclosure attached: “I may earn from qualifying purchases through this look.”</p>}
          <button onClick={() => native(lUrl || cUrl, collection.name)} data-testid="share-native" className="h-11 w-full rounded-full bg-brand text-white font-bold text-sm">{navigator.share ? "Share with an app" : "Copy link"}</button>
          {collection.is_demo && <p className="text-[11px] text-ink-muted">Demo collection — the link opens this illustrative page.</p>}
          <p className="text-[11px] text-ink-muted" data-testid="share-preview-note">Links show the collection name and picture in chat previews, then open this page.</p>
        </Modal>
      )}
    </>
  );
}

export function ContributorLine({ c, avatar = true }) {
  return <Link to={`/u/${c.handle}`} className="inline-flex items-center gap-2 group" data-testid={`contributor-link-${c.handle}`}>{avatar && <Avatar initials={c.initials} size="sm" />}<span className="text-sm"><span className="font-bold text-ink group-hover:text-brand">{c.display_name}</span> <span className="text-ink-muted">@{c.handle}</span></span></Link>;
}
